import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import { updateCoverImages, updateChoices } from './utils/layout';
import { ThemeProvider } from './contexts/ThemeContext';

import { asset } from './utils/asset';

// Make asset function available globally
window.asset = asset;

// Layouts
import InternalLayout from './layouts/InternalLayout';
import AuthLayout from './layouts/AuthLayout';

// Auth Pages
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import SignupSuccess from './pages/auth/SignupSuccess';
import ForgotPassword from './pages/auth/ForgotPassword';
import ChangePassword from './pages/auth/ChangePassword';
import OnBoarding from './pages/auth/OnBoarding';
import OnBoarding2 from './pages/auth/OnBoarding2';

// App Pages
import Dashboard from './pages/Dashboard';
import Personalization from './pages/Personalization';
import JobPortal from './pages/JobPortal';
import Restaurant from './pages/Restaurant';
import FinanceDashboard from './pages/FinanceDashboard';
import SocialDashboard from './pages/SocialDashboard';
import ChatMessage from './pages/ChatMessage';
import Email from './pages/Email';
import Explorer from './pages/Explorer';
import Calendar from './pages/Calendar';
import CompanyProfile from './pages/CompanyProfile';
import CompanyCareer from './pages/CompanyCareer';
import CompanyHelpCenter from './pages/CompanyHelpCenter';
import CompanyContactUs from './pages/CompanyContactUs';
import Forum from './pages/Forum';
import ForumDetails from './pages/ForumDetails';


// Profile Pages
import MyProfile from './pages/profile/MyProfile';
import MyAccount from './pages/profile/MyAccount';
import AccountSettings from './pages/profile/AccountSettings';
import Earning from './pages/profile/Earning';
import Statement from './pages/profile/Statement';
import Subscription from './pages/profile/Subscription';
import SubscriptionPlan from './pages/profile/SubscriptionPlan';

// Extras Pages
import ComingSoon from './pages/extras/ComingSoon';
import Maintenance from './pages/extras/Maintenance';
import PageNotFound from './pages/extras/PageNotFound';
import ExtraLayout from './layouts/ExtraLayout';

import NProgress from 'nprogress';
import 'nprogress/nprogress.css';

// Configure NProgress
NProgress.configure({ showSpinner: false });

const RouteObserver = () => {
    const location = useLocation();

    useEffect(() => {
        // Start progress bar
        NProgress.start();

        updateCoverImages();
        updateChoices();
        // Scroll to top on navigation
        window.scrollTo(0, 0);

        // Run again after a short delay to handle any dynamic content or transitions
        const timer = setTimeout(() => {
            updateCoverImages();
            NProgress.done();
        }, 200);

        const timer2 = setTimeout(updateChoices, 200);

        return () => {
            clearTimeout(timer);
            clearTimeout(timer2);
            NProgress.done();
        };
    }, [location]);

    return null;
};

const App: React.FC = () => {
    return (
        <ThemeProvider>
            <Router basename={import.meta.env.BASE_URL}>
                <RouteObserver />
                <Routes>
                    {/* Default redirect to login */}
                    <Route path="/" element={<Navigate to="/auth/login" replace />} />

                    {/* Auth routes */}
                    <Route element={<AuthLayout />}>
                        <Route path="/auth/login" element={<Login />} />
                        <Route path="/auth/signup" element={<Signup />} />
                        <Route path="/auth/signup-success" element={<SignupSuccess />} />
                        <Route path="/auth/forgot-password" element={<ForgotPassword />} />
                        <Route path="/auth/change-password" element={<ChangePassword />} />
                        <Route path="/auth/onboarding" element={<OnBoarding />} />
                        <Route path="/auth/onboarding2" element={<OnBoarding2 />} />

                    </Route>

                    {/* Internal routes */}
                    <Route element={<InternalLayout />}>
                        <Route path="/app/dashboard" element={<Dashboard />} />
                        <Route path="/app/personalization" element={<Personalization />} />
                        <Route path="/app/my-account" element={<MyAccount />} />
                        <Route path="/app/my-profile" element={<MyProfile />} />
                        <Route path="/app/account-settings" element={<AccountSettings />} />
                        <Route path="/app/earning" element={<Earning />} />
                        <Route path="/app/statement" element={<Statement />} />
                        <Route path="/app/subscription" element={<Subscription />} />
                        <Route path="/app/subscription-plan" element={<SubscriptionPlan />} />
                        <Route path="/app/job-portal" element={<JobPortal />} />
                        <Route path="/app/restaurant" element={<Restaurant />} />
                        <Route path="/app/finance-dashboard" element={<FinanceDashboard />} />
                        <Route path="/app/social-dashboard" element={<SocialDashboard />} />
                        <Route path="/app/chat-message" element={<ChatMessage />} />
                        <Route path="/app/email" element={<Email />} />
                        <Route path="/app/explorer" element={<Explorer />} />
                        <Route path="/app/calendar" element={<Calendar />} />
                        <Route path="/app/company-profile" element={<CompanyProfile />} />
                        <Route path="/app/company-career" element={<CompanyCareer />} />
                        <Route path="/app/company-help-center" element={<CompanyHelpCenter />} />
                        <Route path="/app/company-contact-us" element={<CompanyContactUs />} />
                        <Route path="/app/forum" element={<Forum />} />
                        <Route path="/app/forum-details" element={<ForumDetails />} />

                    </Route>

                    {/* Extras routes */}
                    <Route element={<ExtraLayout />}>
                        <Route path="/extras/coming-soon" element={<ComingSoon />} />
                        <Route path="/extras/maintenance" element={<Maintenance />} />
                        <Route path="/extras/404" element={<PageNotFound />} />
                    </Route>


                    {/* Catch all - redirect to 404 */}
                    <Route path="*" element={<Navigate to="/extras/404" replace />} />
                </Routes>
            </Router>
        </ThemeProvider>
    );
};

export default App;
